// The adaptive score (§13.4): a lookahead sequencer that plays one compiled song on a TickClock.
// Steps (16ths) map to beats, swing warps them, the TempoMap turns beats into round ticks and the
// clock turns ticks into context time, so the downbeat of bar 0 lands exactly on round tick 0 and
// Sudden Death switches tempo on its bar line. Layers follow the phase of the bar being scheduled
// (exact, from the tick) and the live mood (fewest alive, match point, golden, the listener's own
// standing), applied from the next step.
import { RoundPhase } from '@shared/ids';
import { mulberry32 } from '../util';
import { STEPS_PER_BAR, type CompiledBar, type CompiledSong, type DrumEv, type NoteEv } from './arrangement';
import { swingBeat, type TempoMap, type TickClock } from './clock';
import { DRUMS } from './instruments';
import { Pad, type SampledInstrument, type Sampler } from './sampler';
import type { DrumId } from './songTypes';

export type Layer = 'kick' | 'snare' | 'hat' | 'perc' | 'bass' | 'chords' | 'lead' | 'arp' | 'stabs' | 'counter' | 'sparkle' | 'riser';

/** Base level per layer, dB (the song's `mix` adds to it). */
const LAYER_DB: Readonly<Record<Layer, number>> = {
  kick: 0, snare: -3, hat: -3, perc: -5, bass: -3, chords: -7, lead: -1, arp: -5, stabs: -5, counter: -6, sparkle: -10, riser: 3,
};
/**
 * Stereo image of the arrangement (StereoPanner position per layer): the rhythm section and the
 * lead in the middle, the comping and the arp on opposite sides, hats and percussion off-centre.
 */
const LAYER_PAN: Readonly<Record<Layer, number>> = {
  kick: 0, snare: 0.04, hat: 0.32, perc: -0.4, bass: 0, chords: -0.34, lead: 0.06, arp: 0.46, stabs: -0.16, counter: -0.36, sparkle: 0.5, riser: 0,
};
const CRASH_PAN = 0.18;
const CRASH_DB = -5;
const PAD_DB = -17;
const DRONE_DB = -19;
/** §13.4 ghost listener detune. */
export const GHOST_DETUNE_CENTS = -15;

/** The live mood the director sets each frame. */
export interface MusicMood {
  /** ≤ 2 beans alive in Royale / Hot Potato. */
  fewAlive: boolean;
  matchPoint: boolean;
  golden: boolean;
  /** The listener is the crowned leader / holds the potato / is a ghost. */
  leader: boolean;
  potato: boolean;
  ghost: boolean;
  /** Non-scoring song (menu, lobby, podium): every layer. */
  full: boolean;
  /** The filtered intermission groove. */
  intermission: boolean;
  /** Round key offset in semitones (round index, Golden). */
  transpose: number;
}

export function defaultMood(): MusicMood {
  return { fewAlive: false, matchPoint: false, golden: false, leader: false, potato: false, ghost: false, full: false, intermission: false, transpose: 0 };
}

export interface SongPlayerOpts {
  song: CompiledSong;
  tempo: TempoMap;
  clock: TickClock;
  /** Phase at a round tick (locked rounds); free songs are CALM. */
  phaseAt: (tick: number) => RoundPhase;
  mood: MusicMood;
  dest: AudioNode;
  sampler: Sampler;
  /** First step to schedule (−32: the two intro bars before tick 0). */
  startStep: number;
  /** Bar-line hook (ViewClock.barLine): true if the clock re-anchored. */
  onBarLine?: () => boolean;
  /** Per-song low-pass (the filtered intermission groove), Hz. */
  lowpass?: number;
  seed?: number;
  /** Only these layers sound (analysis renders); crashes and pads follow 'kick' and 'chords'. */
  only?: ReadonlySet<Layer>;
  /** A riser swells through the pickup bar into the round's downbeat (locked rounds). */
  introRiser?: boolean;
}

type StepIndex<T> = T[][];

function byStep<T extends { step: number }>(evs: readonly T[]): StepIndex<T> {
  const out: T[][] = Array.from({ length: STEPS_PER_BAR }, () => []);
  for (const e of evs) out[Math.min(STEPS_PER_BAR - 1, Math.floor(e.step))].push(e);
  return out;
}

interface IndexedBar {
  bar: CompiledBar;
  kick: StepIndex<DrumEv>; snare: StepIndex<DrumEv>; hat: StepIndex<DrumEv>; perc: StepIndex<DrumEv>;
  bass: StepIndex<NoteEv>; chords: StepIndex<NoteEv>; lead: StepIndex<NoteEv>; arp: StepIndex<NoteEv>;
  stabs: StepIndex<NoteEv>; counter: StepIndex<NoteEv>; sparkle: StepIndex<NoteEv>;
}

export interface ScheduledNote { layer: string; step: number; time: number; midi: number }

export class SongPlayer {
  readonly out: GainNode;
  private readonly filter: BiquadFilterNode | null = null;
  private readonly bars: IndexedBar[];
  private readonly intro: { kick: StepIndex<DrumEv>; snare: StepIndex<DrumEv>; hat: StepIndex<DrumEv>; perc: StepIndex<DrumEv>; bass: StepIndex<NoteEv> }[];
  private readonly dt: { kick: StepIndex<DrumEv>; snare: StepIndex<DrumEv>; hat: StepIndex<DrumEv> };
  private readonly heart: StepIndex<DrumEv>;
  private readonly trans: { kick: StepIndex<DrumEv>; snare: StepIndex<DrumEv>; hat: StepIndex<DrumEv>; perc: StepIndex<DrumEv> };
  /** The bar playing the transition fill into a phase entry (NaN: none). */
  private transitionBar = Number.NaN;
  /** The mood the layers follow: the live mood, latched on each beat so a change (double-time,
   * MATCH POINT, the crown) enters on a beat instead of cutting in mid-beat. */
  private readonly lat: MusicMood = defaultMood();
  private latched = false;
  private next: number;
  private lastBarChecked = Number.NaN;
  private prevPhase: RoundPhase | null = null;
  private stopped = false;
  private readonly rand: () => number;
  private pad: Pad | null = null;
  private drone: Pad | null = null;
  private readonly unit: 1 | 2;
  /** Optional log of scheduled notes (tests, the soundboard). */
  log: ScheduledNote[] | null = null;
  /** Steps whose accent kick is already booked. */
  private readonly accents = new Set<number>();
  /** Per-layer panners into `out` (created on first use). */
  private readonly lanes = new Map<Layer | 'crash', StereoPannerNode>();
  private readonly laneGains: GainNode[] = [];

  constructor(private readonly ctx: BaseAudioContext, private readonly o: SongPlayerOpts) {
    this.out = ctx.createGain();
    this.out.gain.value = 10 ** ((o.song.def.level ?? 0) / 20);
    if (o.lowpass) {
      this.filter = ctx.createBiquadFilter();
      this.filter.type = 'lowpass';
      this.filter.frequency.value = o.lowpass;
      this.filter.Q.value = 0.8;
      this.out.connect(this.filter).connect(o.dest);
    } else {
      this.out.connect(o.dest);
    }
    const s = o.song;
    this.bars = s.bars.map((bar) => ({
      bar,
      kick: byStep(bar.kick), snare: byStep(bar.snare), hat: byStep(bar.hat), perc: byStep(bar.perc),
      bass: byStep(bar.bass), chords: byStep(bar.chords), lead: byStep(bar.lead), arp: byStep(bar.arp),
      stabs: byStep(bar.stabs), counter: byStep(bar.counter), sparkle: byStep(bar.sparkle),
    }));
    this.intro = s.intro.map((b) => ({ kick: byStep(b.kick), snare: byStep(b.snare), hat: byStep(b.hat), perc: byStep(b.perc), bass: byStep(b.bass) }));
    this.dt = { kick: byStep(s.doubleTime.kick), snare: byStep(s.doubleTime.snare), hat: byStep(s.doubleTime.hat) };
    this.heart = byStep(s.heartbeat);
    this.trans = { kick: byStep(s.transition.kick), snare: byStep(s.transition.snare), hat: byStep(s.transition.hat), perc: byStep(s.transition.perc) };
    this.next = o.startStep;
    this.rand = mulberry32(o.seed ?? 1);
    this.unit = s.def.swingUnit === 8 ? 2 : 1;
  }

  /** The live mood this player reads at every step (the director writes it each frame). */
  get mood(): MusicMood {
    return this.o.mood;
  }

  get song(): CompiledSong {
    return this.o.song;
  }

  get tempo(): TempoMap {
    return this.o.tempo;
  }

  get clock(): TickClock {
    return this.o.clock;
  }

  get isStopped(): boolean {
    return this.stopped;
  }

  /** Context time of (fractional) step `x`, swing included. */
  timeOfStep(x: number): number {
    const beat = swingBeat(x / 4, this.o.song.def.swing, this.unit);
    return this.o.clock.audioTime(this.o.tempo.tickAtBeat(beat));
  }

  /** Unswung step position at context time `t`. */
  stepAt(t: number): number {
    return this.o.tempo.beatAtTick(this.o.clock.tickAt(t)) * 4;
  }

  /**
   * The next grid point (every `unitSteps` 16ths from step `offset`, swung) at or after
   * `t + margin`. `nextGrid(t, 8, 4)` is the next backbeat (beat 2 or 4).
   */
  nextGrid(t: number, unitSteps: number, margin = 0.004, offset = 0): number {
    const at = t + margin;
    let s = Math.ceil((this.stepAt(at) - offset) / unitSteps) * unitSteps + offset;
    let time = this.timeOfStep(s);
    let guard = 0;
    while (time < at && guard++ < 8) {
      s += unitSteps;
      time = this.timeOfStep(s);
    }
    return time;
  }

  /** Transposition and chord sounding at context time `t`. */
  harmonyAt(t: number): { tonic: number; bar: CompiledBar; phase: RoundPhase } {
    const step = Math.floor(this.stepAt(t));
    const bar = Math.floor(step / STEPS_PER_BAR);
    const tick = this.o.tempo.tickAtBeat(step / 4);
    const phase = this.o.phaseAt(tick);
    const sb = ((bar % 16) + 16) % 16;
    return { tonic: this.o.song.tonic + this.transposeFor(phase), bar: this.o.song.bars[sb], phase };
  }

  private transposeFor(phase: RoundPhase): number {
    return this.o.mood.transpose + (phase === RoundPhase.SUDDEN_DEATH ? 1 : 0);
  }

  /** Schedules every step whose time is before `horizon`. */
  schedule(horizon: number): void {
    if (this.stopped) return;
    const now = this.ctx.currentTime;
    // Far behind (a throttled tab): jump to the present instead of playing a burst of late notes.
    if (this.timeOfStep(this.next) < now - 0.1) {
      const cur = Math.ceil(this.stepAt(now));
      if (Number.isFinite(cur)) this.next = Math.max(this.next, cur);
    }
    // Live, 120 ms holds a handful of steps; offline renders schedule whole songs in one call.
    let guard = 0;
    while (guard++ < 8192) {
      const s = this.next;
      if (s % STEPS_PER_BAR === 0 && this.lastBarChecked !== s) {
        this.lastBarChecked = s;
        this.o.onBarLine?.();
      }
      const t = this.timeOfStep(s);
      if (!Number.isFinite(t) || t > horizon) break;
      if (t >= now - 0.004) this.scheduleStep(s, t);
      this.next++;
    }
  }

  /** §13.4 "the beat slams back": an accent kick (and a light crash) on the next 16th. */
  accent(t: number): number {
    const at = this.nextGrid(t, 1);
    const step = Math.round(this.stepAt(at + 0.001));
    if (this.accents.has(step)) return at;
    this.accents.add(step);
    const kit = this.o.song.def.kit;
    this.o.sampler.drum(kit.kick, at, 1, this.lane('kick'), 4);
    this.o.sampler.drum(kit.crash ?? 'crash', at, 0.6, this.lane('crash'), CRASH_DB - 4);
    return at;
  }

  stop(at: number, fade: number): void {
    if (this.stopped) return;
    this.stopped = true;
    const g = this.out.gain;
    const t = Math.max(at, this.ctx.currentTime);
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(0, t + fade);
    this.pad?.release(t, fade);
    this.drone?.release(t, fade);
    const ms = (t - this.ctx.currentTime + fade + 0.5) * 1000;
    setTimeout(() => {
      this.out.disconnect();
      this.filter?.disconnect();
      for (const l of this.lanes.values()) l.disconnect();
      for (const g of this.laneGains) g.disconnect();
    }, Math.max(0, ms));
  }

  // ---- Step scheduling ------------------------------------------------------------------------

  private layersFor(phase: RoundPhase, m: MusicMood): Set<Layer> | 'heartbeat' {
    if (m.matchPoint && !m.full) return 'heartbeat';
    const l = new Set<Layer>(['kick', 'snare', 'bass', 'chords']);
    if (m.intermission) {
      l.delete('snare');
      for (const x of ['hat', 'arp', 'perc'] as const) l.add(x);
      return l;
    }
    if (m.full || phase === RoundPhase.RAMP || phase === RoundPhase.SUDDEN_DEATH || phase === RoundPhase.OVER) {
      for (const x of ['lead', 'hat', 'arp', 'perc'] as const) l.add(x);
    }
    if (phase === RoundPhase.SUDDEN_DEATH) l.add('sparkle');
    if (m.fewAlive && !m.full) {
      l.delete('chords');
      l.add('lead');
    }
    if (m.golden) l.add('stabs');
    if (m.leader) l.add('counter');
    return l;
  }

  private human(spread: number): number {
    return (this.rand() * 2 - 1) * spread;
  }

  private scheduleStep(s: number, t: number): void {
    const bar = Math.floor(s / STEPS_PER_BAR);
    const sib = s - bar * STEPS_PER_BAR;
    if (bar < -2) return;
    const def = this.o.song.def;
    const mix = def.mix ?? {};
    if (!this.latched || sib % 4 === 0) {
      Object.assign(this.lat, this.o.mood);
      this.latched = true;
    }
    const m = this.lat;
    const tick = this.o.tempo.tickAtBeat(s / 4);
    const phase = this.o.phaseAt(tick);
    const transpose = this.transposeFor(phase);
    const detune = m.ghost ? GHOST_DETUNE_CENTS : 0;
    const smp = this.o.sampler;
    const stepSec = this.timeOfStep(s + 1) - t;

    const only = this.o.only;
    const drums = (evs: readonly DrumEv[], layer: Layer, extraDb = 0): void => {
      if (only && !only.has(layer)) return;
      for (const e of evs) {
        const at = e.step === sib ? t : this.timeOfStep(bar * STEPS_PER_BAR + e.step);
        const vel = e.vel * (1 + this.human(0.06));
        const jitter = layer === 'kick' || layer === 'snare' ? 0 : this.human(0.002);
        smp.drum(e.drum, at + jitter, vel, this.lane(layer), LAYER_DB[layer] + (mix[layer as keyof typeof mix] ?? 0) + extraDb);
        this.log?.push({ layer, step: s, time: at, midi: -1 });
      }
    };
    const notes = (evs: readonly NoteEv[], inst: SampledInstrument, layer: Layer, strum = 0, extraDb = 0): void => {
      if (only && !only.has(layer)) return;
      const list = strum > 0 ? [...evs].sort((a, b) => a.midi - b.midi) : evs;
      list.forEach((e, i) => {
        const dur = Math.max(0.03, this.timeOfStep(bar * STEPS_PER_BAR + e.step + e.dur) - t - 0.01);
        const at = t + i * strum + this.human(layer === 'bass' ? 0.001 : 0.003);
        const midi = e.midi + transpose;
        smp.note(inst, midi, at, dur, e.vel * (1 + this.human(0.05)), this.lane(layer), detune, LAYER_DB[layer] + (mix[layer as keyof typeof mix] ?? 0) + extraDb);
        this.log?.push({ layer, step: s, time: at, midi });
      });
    };

    if (bar < 0) {
      const ib = this.intro[bar + 2];
      if (!ib) return;
      if (bar === -1 && sib === 0 && this.o.introRiser && (!only || only.has('riser'))) this.riser(0);
      drums(ib.hat[sib], 'hat');
      drums(ib.kick[sib], 'kick');
      drums(ib.snare[sib], 'snare');
      drums(ib.perc[sib], 'perc');
      notes(ib.bass[sib], def.instruments.bass as SampledInstrument, 'bass');
      return;
    }

    const sb = bar % 16;
    const ib = this.bars[sb];
    const layers = this.layersFor(phase, m);
    // Free-running songs (menu, lobby, podium) are listened to for minutes: a four-pass cycle
    // plays the A half instrumental on the second pass and the B half on the fourth, with the arp
    // stepping forward into the space the melody leaves.
    const tacet = layers !== 'heartbeat' && instrumentalHalf(bar, m);
    if (tacet) (layers as Set<Layer>).delete('lead');

    // Phrase dynamics: the B half sits 1 dB up and each 8-bar phrase swells into its last bars.
    const phraseDb = (sb >= 8 ? 1 : 0) + ((sb % 8) >= 6 ? 0.8 : 0);

    if (sib === 0) {
      const entering = this.prevPhase !== null && phase !== this.prevPhase && (phase === RoundPhase.RAMP || phase === RoundPhase.SUDDEN_DEATH);
      const roundStart = s === 0 && this.o.startStep < 0;
      const loopTop = (sb === 0 || sb === 8) && bar > 0 && (layers === 'heartbeat' ? false : layers.has('lead'));
      if ((entering || roundStart || loopTop) && (!only || only.has('kick'))) smp.drum(def.kit.crash ?? 'crash', t, entering || roundStart ? 1 : 0.7, this.lane('crash'), CRASH_DB + (mix.hat ?? 0) * 0.5);
      this.prevPhase = phase;
      // The bar before a phase entry (RAMP, SUDDEN DEATH: both start on a bar line, §5) plays the
      // song's fill, with a riser that peaks on the entry's downbeat crash.
      const nextPhase = this.o.phaseAt(this.o.tempo.tickAtBeat((bar + 1) * 4));
      const into = nextPhase !== phase && (nextPhase === RoundPhase.RAMP || nextPhase === RoundPhase.SUDDEN_DEATH);
      this.transitionBar = into && !m.full && !m.intermission ? bar : Number.NaN;
      if (this.transitionBar === bar && (!only || only.has('riser'))) this.riser((bar + 1) * STEPS_PER_BAR);
      if (!only || only.has('chords')) this.updatePads(layers, ib.bar, t, transpose, detune);
    }

    if (layers === 'heartbeat') {
      drums(this.heart[sib], 'kick', 2);
      return;
    }

    const fill = this.transitionBar === bar;
    if (m.fewAlive && !m.full) {
      drums(this.dt.kick[sib], 'kick', phraseDb);
      drums(this.dt.snare[sib], 'snare', phraseDb);
      drums(this.dt.hat[sib], 'hat', phraseDb);
    } else if (fill) {
      drums(this.trans.kick[sib], 'kick', phraseDb);
      drums(this.trans.snare[sib], 'snare', phraseDb + 1);
      if (layers.has('hat')) drums(this.trans.hat[sib], 'hat', phraseDb);
    } else {
      if (layers.has('kick')) drums(ib.kick[sib], 'kick', phraseDb + (m.intermission ? -4 : 0));
      if (layers.has('snare')) drums(ib.snare[sib], 'snare', phraseDb);
      if (layers.has('hat')) drums(ib.hat[sib], 'hat', phraseDb);
    }
    if (layers.has('perc')) drums(fill ? this.trans.perc[sib] : ib.perc[sib], 'perc', phraseDb);
    if (layers.has('bass')) notes(ib.bass[sib], def.instruments.bass as SampledInstrument, 'bass', 0, phraseDb);
    if (layers.has('chords')) {
      const strum = def.instruments.chords === 'ep' || def.instruments.chords === 'nylon' ? Math.min(0.012, stepSec * 0.08) : def.instruments.chords === 'accordion' ? 0.003 : 0;
      notes(ib.chords[sib], def.instruments.chords as SampledInstrument, 'chords', strum, phraseDb);
    }
    if (layers.has('lead')) notes(ib.lead[sib], def.instruments.lead as SampledInstrument, 'lead', 0, phraseDb);
    if (layers.has('arp') && def.instruments.arp) notes(ib.arp[sib], def.instruments.arp as SampledInstrument, 'arp', 0, phraseDb + (tacet ? 3 : 0));
    if (layers.has('stabs')) notes(ib.stabs[sib], 'brass', 'stabs', 0.004);
    if (layers.has('counter')) notes(ib.counter[sib], (def.counter ?? 'brass') as SampledInstrument, 'counter');
    if (layers.has('sparkle')) notes(ib.sparkle[sib], 'bell', 'sparkle');
  }

  /**
   * The panned input of a layer. Equal-power panning puts a centred mono source at −3 dB per
   * channel (a plain mono upmix is 0 dB), so each lane gets √2 back: every layer keeps its power
   * wherever it sits in the image.
   */
  private lane(layer: Layer | 'crash'): AudioNode {
    let p = this.lanes.get(layer);
    if (!p) {
      p = this.ctx.createStereoPanner();
      p.pan.value = layer === 'crash' ? CRASH_PAN : LAYER_PAN[layer];
      const makeup = this.ctx.createGain();
      makeup.gain.value = Math.SQRT2;
      p.connect(makeup).connect(this.out);
      this.lanes.set(layer, p);
      this.laneGains.push(makeup);
    }
    return p;
  }

  /** The transition riser, timed so its peak lands on step `downbeat`. */
  private riser(downbeat: number): void {
    const at = this.timeOfStep(downbeat);
    const dur = DRUMS.riser.dur;
    if (at - dur < this.ctx.currentTime) return;
    this.o.sampler.drum('riser', at - dur, 1, this.lane('riser'), LAYER_DB.riser);
    this.log?.push({ layer: 'riser', step: downbeat, time: at - dur, midi: -1 });
  }

  /** Live pads: the MATCH POINT pad and the listener's potato drone (root and ♭3 of the key). */
  private updatePads(layers: Set<Layer> | 'heartbeat', bar: CompiledBar, t: number, transpose: number, detune: number): void {
    const wantPad = layers === 'heartbeat';
    const wantDrone = this.o.mood.potato && !this.o.mood.full;
    if (wantPad) {
      this.pad ??= new Pad(this.ctx, this.out, PAD_DB, 1400);
      this.pad.set(bar.pad.map((n) => n.midi + transpose), t, detune);
    } else if (this.pad?.playing) {
      this.pad.release(t);
    }
    if (wantDrone) {
      this.drone ??= new Pad(this.ctx, this.out, DRONE_DB, 900);
      const root = this.o.song.tonic + transpose;
      const low = root - 12 * Math.ceil((root - 50) / 12);
      this.drone.set([low, low + 3, low + 12], t, detune, 0.3);
    } else if (this.drone?.playing) {
      this.drone.release(t, 0.5);
    }
  }
}

/** True when bar `bar` of a free-running song is in an instrumental half of its four-pass cycle. */
export function instrumentalHalf(bar: number, m: MusicMood): boolean {
  if (!m.full || m.intermission || bar < 0) return false;
  const pass = Math.floor(bar / 16) % 4;
  const half = Math.floor((bar % 16) / 8);
  return (pass === 1 && half === 0) || (pass === 3 && half === 1);
}

/** Every drum a player may trigger (accent and crash included). */
export function crashOf(song: CompiledSong): DrumId {
  return song.def.kit.crash ?? 'crash';
}
